<template>
  <v-card class="pa-4">
    <div class="table-wrapper">
      <v-data-table-server
        :headers="headers"
        :items="store.tableData"
        :items-length="store.pagination.total"
        :loading="store.loadingFortable"
        :items-per-page="store.pagination.per_page"
        :page="store.pagination.current_page"
        @update:page="onPageChange"
        @update:items-per-page="onPerPageChange"
        class="elevation-0"
        item-key="id"
        id="data-table"
      >
        <template #headers="{ props }">
          <tr>
            <th v-for="header in headers" :key="header.value">
              {{ header.text }}
            </th>
          </tr>
        </template>

        <!-- Name Column -->
        <template #item.name="{ item }">
          <div class="d-flex align-center ga-2">
            <img
              class="platform-img"
              :src="getPlatformIcon(item.type || item.platform)"
            />
            {{ item.name }}
          </div>
        </template>

        <!-- Status -->
        <template #item.status="{ item }">
          <v-switch
            v-model="item.status"
            :true-value="'ACTIVE'"
            :false-value="'PAUSED'"
            density="compact"
            hide-details
            color="#237276"
            :loading="item.loading"
            @change="() => emitStatusChange(item)"
          />
        </template>

        <!-- Delivery Status -->
        <template #item.delivery_status="{ item }">
          <div class="d-flex align-center ga-2">
            <div :class="getDeliveryClass(item)" class="status-circle"></div>
            <div>{{ item.delivery_status?.[0] ?? "Unknown" }}</div>
            <v-tooltip location="top" v-if="item.type !== 'facebook'">
              <template #activator="{ props }">
                <v-icon
                  v-bind="props"
                  icon="mdi-information-outline"
                  size="16"
                  color="grey"
                  class="cursor-pointer"
                />
              </template>

              <div>
                <ul
                  style="list-style: none"
                  v-if="item.delivery_status?.length > 1"
                >
                  <li
                    v-for="(reason, index) in item.delivery_status.slice(1)"
                    :key="index"
                  >
                    {{ reason }}
                  </li>
                </ul>
              </div>
            </v-tooltip>
          </div>
        </template>

        <!-- Amount Spent -->
        <template #item.amount_spent="{ item }">
          ${{ item.stats?.spend ?? 0 }}
        </template>

        <!-- Result -->
        <template #item.result="{ item }">
          {{ item.stats?.native_leads ?? 0 }} leads
        </template>

        <!-- Cost Per Result -->
        <template #item.cost_per_result="{ item }">
          <div>
            {{ formatCurrency(item.calculations?.costPerClick) }}
            <br />
            <span class="text-caption">cost per lead</span>
          </div>
        </template>

        <!-- Impressions -->
        <template #item.paid_impressions="{ item }">
          {{ item.stats?.impressions ?? 0 }}
        </template>

        <!-- eCPM -->
        <template #item.paid_ecpm="{ item }">
          {{ formatCurrency(item.calculations?.eCPM) }}
        </template>

        <!-- Clicks -->
        <template #item.clicks="{ item }">
          {{ item.stats?.clicks ?? 0 }}
        </template>

        <!-- eCPC -->
        <template #item.ecpc="{ item }">
          {{ formatCurrency(item.calculations?.eCPC) }}
        </template>

        <!-- Action Buttons -->
        <template #item.action="{ item }">
          <div class="table-action">
            <!-- <button
              @click="showAnalisyes(item.id, item.ad_account_id, item.type)"
            >
              <img src="@/assets/soft-ware-img/see-analisyes.svg" alt="" />
            </button> -->
            <button @click="copyRowData(item)">
              <img src="@/assets/soft-ware-img/copy-table-action.svg" alt="" />
            </button>
          </div>
        </template>
      </v-data-table-server>
    </div>
  </v-card>
</template>

<script>
import { adsPerformance } from "@/store/AdsPerformance/ads_performance";
import { useGeneralStore } from "@/store/General";
import * as XLSX from "xlsx";

export default {
  name: "CampaignTable",
  data() {
    return {
      store: adsPerformance(),
      generalStore: useGeneralStore(),
      headers: [
        { text: "Name", value: "name" },
        { text: "Status", value: "status" },
        { text: "Delivery Status", value: "delivery_status" },
        { text: "Amount Spent", value: "amount_spent" },
        { text: "Result", value: "result" },
        { text: "Cost per Result", value: "cost_per_result" },
        { text: "Paid Impressions", value: "paid_impressions" },
        { text: "Paid eCPM", value: "paid_ecpm" },
        { text: "Clicks", value: "clicks" },
        { text: "eCPC", value: "ecpc" },
        { text: "Action", value: "action", sortable: false },
      ],
    };
  },
  methods: {
    showAnalisyes(id, ad_account_id, type) {
      this.$router.push(`/ads-performance/${ad_account_id}/${type}/${id}`);
    },
    exportTableToExcel() {
      const table = document.getElementById("data-table");
      const workbook = XLSX.utils.table_to_book(table);
      XLSX.writeFile(workbook, "table-data.xlsx");
    },
    formatLifetimeSpend(value) {
      if (!value || isNaN(value)) return 0;
      return Math.ceil(value / 1_000_000);
    },
    copyRowData(item) {
      const rowData = {
        campaign_name: item.name,
        platform: item.type,
        Amount_Spent: `$${item.stats?.spend ?? 0}`,
        status: item.status,
        cost_per_result: `$${item.calculations?.costPerClick ?? 0}`,
        Paid_Impressions: item.stats?.impressions ?? 0,
        Clicks: item.stats?.clicks ?? 0,
        eCPM: `$${item.calculations?.eCPM ?? 0}`,
        eCPC: `$${item.calculations?.eCPC ?? 0}`,
        Delivery_Status: item.delivery_status?.[0] ?? "Unknown",
      };

      const textToCopy = Object.entries(rowData)
        .map(([key, value]) => `${key}: ${value}`)
        .join("\n");

      navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
          this.generalStore.showSnackbar(
            "Row data copied to clipboard",
            "#237276"
          );
        })
        .catch((err) => {
          console.error("Failed to copy text: ", err);
          this.generalStore.showSnackbar("Failed to copy data", "error");
        });
    },
    formatCurrency(value) {
      const number = parseFloat(value);
      if (isNaN(number) || !isFinite(number)) return "$0.00";
      return `$${number.toFixed(2)}`;
    },
    getDeliveryClass(item) {
      const mainStatus = item.delivery_status?.[0];
      if (mainStatus === "Delivering") return "delivering";
      else if (mainStatus === "Not Delivering") return "not-delivering";
      else return "unknown-status";
    },
    emitStatusChange(item) {
      this.$emit("change-status", item);
    },
    onPageChange(newPage) {
      this.store.getData(newPage, this.store.pagination.per_page);
    },
    onPerPageChange(newPerPage) {
      this.store.getData(1, newPerPage);
    },
    getPlatformIcon(platform) {
      switch (platform) {
        case "facebook":
          return require("@/assets/soft-ware-img/facebook-post-select.svg");
        case "instagram":
          return "https://cdn-icons-png.flaticon.com/512/2111/2111463.png";
        case "snapchat":
          return require("@/assets/soft-ware-img/snapchat-icoon-ccampaign.svg");
        case "tiktok":
          return require("@/assets/soft-ware-img/tik-tok-select-icon.svg");
        case "linkedin":
          return "https://cdn-icons-png.flaticon.com/512/145/145807.png";
        default:
          return "https://cdn-icons-png.flaticon.com/512/565/565547.png";
      }
    },
  },
};
</script>

<style scoped>
.v-data-table {
  font-size: 14px;
}
.platform-img {
  width: 2rem;
}
.status-circle {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
.delivering {
  background-color: #00796b;
}
.not-delivering {
  background-color: #cfcfcf;
}
.unknown-status {
  background-color: #ff0000;
}
.v-data-table-footer {
  justify-content: space-between !important;
}
.table-wrapper {
  overflow-x: auto;
}
.table-wrapper > .v-data-table {
  min-width: 1200px;
}
</style>
